/**
 * 68.0: the order forms the owner's commanding UI needs (DIRECTION §0.6, the verbs table), built in the Match so the
 * L0 menu (lane C, 68.2–68.3) and the table (lane D, 68.4) only build interface:
 *
 * - **move** at a position — a task: go there at the formation, fighting as it goes, then hold;
 * - **hold / bound / flank** at a position — the position forms of today's point-id orders (an `OrderPlace` is a point
 *   id or a planet + surface direction; `orderPlace` resolves either);
 * - **engage** a target — a task naming an enemy unit, valid only while the team *knows* it (the awareness core's
 *   fairness rule: in a teammate's sight, or in the team picture); it lapses when the target dies or is lost;
 * - **crew** a structure — a task naming a seat.
 *
 * Every form is an `order` input through `apply` with the actor rules the existing orders have (core/squads.ts
 * applyOrderInput); refused with a reason, logged, stored on the squad as a task with expiry. Nothing here moves a
 * result until a brain obeys a form: the read side (`squadTaskFor`, `orderPlace`, `orderTarget`) is for lane C's 68.2.
 */
import { structureJob } from './body';
import { drives, structureTeam, cargoOnly } from './body';
import type { GameState, PlayerState, SquadOrder, Vec3 } from '../types';
import type { OrderBody } from './squads';
import { getVisibleEnemies } from './fog';
import { teamPicture } from './picture';
import { seatAt } from './seats';
import { AMBUSH } from '../constants';

/** Where an order points: a capture point by id, or a place on a planet. */
export type OrderPlace = { pointId: number; planetIndex?: undefined; surfaceDir?: undefined } | { pointId?: undefined; planetIndex: number; surfaceDir: Vec3 };

/** The kinds that carry a place. */
export type PlacedOrder = Extract<SquadOrder, { kind: 'take' | 'hold' | 'bound' | 'flank' | 'move' | 'ride' }>;

/** The place an order names, resolved: a point's planet and direction, or the position itself; null when the point is gone. */
export function orderPlace(state: Pick<GameState, 'modeState'>, order: PlacedOrder): { planetIndex: number; surfaceDir: Vec3 } | null {
  if (order.pointId !== undefined) {
    const site = (state.modeState.points?.sites ?? []).find(s => s.id === order.pointId);
    return site ? { planetIndex: site.planetIndex, surfaceDir: site.surfaceDir } : null;
  }
  return order.planetIndex !== undefined && order.surfaceDir ? { planetIndex: order.planetIndex, surfaceDir: order.surfaceDir } : null;
}

/** The unit an engage order names, if it still lives; null otherwise. */
export function orderTarget(state: Pick<GameState, 'players'>, order: Extract<SquadOrder, { kind: 'engage' }>): PlayerState | null {
  const u = state.players.find(p => p.id === order.unitId);
  return u && u.alive ? u : null;
}

/**
 * The knowledge limit: does `team` know enemy `unitId` now — in a living teammate's sight (line of sight, the
 * perception layer applied), or in the team picture (a track, a sighting, a mark that names it)? The target must live.
 */
export function teamKnowsUnit(state: GameState, team: number, unitId: number, tick: number, useWasm?: boolean): boolean {
  const target = state.players.find(p => p.id === unitId);
  if (!target || !target.alive || target.teamIndex === team) return false;
  if (teamPicture(state, team, tick).some(b => b.unitId === unitId)) return true;
  const viewer = state.players.find(p => p.teamIndex === team && p.alive);
  if (!viewer) return false;
  for (const mate of state.players) {
    if (mate.teamIndex !== team || !mate.alive) continue;
    if (getVisibleEnemies(mate, state.players, state.planets, useWasm, state).includes(unitId)) return true;
  }
  return false;
}

export type FormRefusal = { refused: string; code: string } | null;

/** The 68.0 forms' own rules at apply (the tier and actor rules are applyOrderInput's). Null = nothing to refuse. */
export function validateOrderForm(state: GameState, body: OrderBody, tick: number, useWasm?: boolean): FormRefusal {
  const order = body.order as SquadOrder;
  const placed = (o: PlacedOrder) => {
    if (o.pointId === undefined && (o.planetIndex === undefined || !o.surfaceDir)) return { refused: 'An order needs a point or a place', code: 'place' };
    if (o.planetIndex !== undefined && !state.planets[o.planetIndex]?.alive) return { refused: `No planet ${o.planetIndex} to go to`, code: 'planet' };
    return null;
  };
  switch (order.kind) {
    case 'move':
      if (body.tier !== 'task') return { refused: 'A move is a task, not a posture', code: 'tier' };
      return placed(order);
    case 'hold':
      // 66.11 the ambush: the cover system's (posts hidden from the way in), so only with its switch on for the side
      if (order.ambush) {
        const cover = state.config.coverMoves;
        if (!(Array.isArray(cover) ? cover[body.team] !== false : cover !== false)) return { refused: 'Ambush needs Bots hold from cover', code: 'switch' };   // (66.19: on unless false)
        const z = order.ambush.zonePeople;
        if (z !== undefined && !(z >= AMBUSH.minZone && z <= AMBUSH.maxZone)) return { refused: `An ambush's zone is ${AMBUSH.minZone}–${AMBUSH.maxZone} people`, code: 'zone' };
      }
      return placed(order);
    case 'take': case 'bound': case 'flank':
      return placed(order);
    case 'engage': {
      if (body.tier !== 'task') return { refused: 'Engage is a task, not a posture', code: 'tier' };
      const target = state.players.find(p => p.id === order.unitId);
      if (!target || !target.alive) return { refused: `No living unit ${order.unitId} to engage`, code: 'target' };
      if (target.teamIndex === body.team) return { refused: `${target.name} is on your side`, code: 'team' };
      if (!teamKnowsUnit(state, body.team, order.unitId, tick, useWasm)) return { refused: `Nobody on the team knows where ${target.name} is`, code: 'unknown' };
      return null;
    }
    case 'ride': {
      // 63.1d step 4: a transport of your side (or nobody's), standing, with bays; and where to
      if (body.tier !== 'task') return { refused: 'A ride is a task, not a posture', code: 'tier' };
      const st = state.structures.find(s => s.id === order.structureId);
      if (!st || st.health <= 0 || !drives(st)) return { refused: `No standing vehicle ${order.structureId}`, code: 'structure' };
      // 69.4 step 5 (DECISIONS 166): the supply truck goes on supply runs only — a convoy's leg, never a ride to the fight
      if (cargoOnly(st)) { if (!order.convoy) return { refused: `Vehicle ${order.structureId} carries supply only`, code: 'carry' }; }
      else if (!(structureJob(st).carry ?? 0)) return { refused: `Vehicle ${order.structureId} carries nobody`, code: 'carry' };
      const owner = state.players.find(p => p.id === st.ownerId);
      if (owner && owner.teamIndex !== body.team) return { refused: 'That vehicle is the enemy\'s', code: 'team' };
      return placed(order);
    }
    case 'dismount': {
      // 63.1i: a task, given while someone it names sits in a seat (in that structure, when it names one)
      if (body.tier !== 'task') return { refused: 'Dismount is a task, not a posture', code: 'tier' };
      const ids = 'units' in body.to ? body.to.units : (state.squads?.find(s => s.id === (body.to as { squad: number }).squad)?.unitIds ?? []);
      if (!ids.some(id => seatedFor(state, id, order))) return { refused: order.structureId === undefined ? 'Nobody of theirs is in a seat' : `Nobody of theirs is in ${order.structureId}`, code: 'seated' };
      return null;
    }
    case 'demolish': {
      // 69.5d step 2: a standing structure, not of your side
      if (body.tier !== 'task') return { refused: 'Demolish is a task, not a posture', code: 'tier' };
      const st = state.structures.find(s => s.id === order.structureId);
      if (!st || st.health <= 0) return { refused: `No standing structure ${order.structureId}`, code: 'structure' };
      if (structureTeam(state, st) === body.team) return { refused: 'That structure is your side\'s', code: 'team' };
      return null;
    }
    case 'crew': {
      if (body.tier !== 'task') return { refused: 'Crewing is a task, not a posture', code: 'tier' };
      const st = state.structures.find(s => s.id === order.structureId);
      if (!st || st.health <= 0) return { refused: `No standing structure ${order.structureId}`, code: 'structure' };
      if (!seatAt(st, order.seat)) return { refused: `Structure ${order.structureId} has no seat ${order.seat}`, code: 'seat' };
      return null;
    }
    default: return null;
  }
}

/**
 * A form that has lapsed on its own: an engage whose target is dead, a crew whose seat is gone. Read by the order
 * readers (such a task no longer counts) and dropped by pruneTasks. Losing the target is pruneTasks' alone (it costs a
 * sight test): until then the brain keeps the last place it knew.
 */
/** 63.1i: whether a unit is seated where a dismount reaches — in any seat, or in the structure it names. */
export function seatedFor(state: Partial<Pick<GameState, 'players'>>, unitId: number, order: { structureId?: number }): boolean {
  const p = state.players?.find(q => q.id === unitId);
  return !!p?.alive && !!p.mount && (order.structureId === undefined || p.mount.bodyId === order.structureId);
}

/**
 * 63.1i: the tasks a dismount supersedes — the crew and ride tasks issued before it, of the structure it names (or any).
 * A dismount never lapses by itself (a reader skips it for a member on foot, so it lapses for each as it gets out); kept,
 * it keeps the squad out of the seats it was told to leave until a newer crew or ride order.
 * 69.4 step 5: so does a convoy's home leg its outward leg in that truck — lapsed or not (it lapses once the truck
 * stands home, and its driver goes back to its standing order, not to the FOB).
 */
export function dismountSupersedes(dismount: SquadOrder, older: SquadOrder): boolean {
  if (dismount.kind === 'ride' && dismount.convoy?.leg === 'back') return older.kind === 'ride' && older.convoy?.leg === 'out' && older.structureId === dismount.structureId;
  if (dismount.kind !== 'dismount' || (older.kind !== 'crew' && older.kind !== 'ride')) return false;
  return dismount.structureId === undefined || dismount.structureId === older.structureId;
}

export function orderFormLapsed(state: Partial<Pick<GameState, 'players' | 'structures'>>, order: SquadOrder): boolean {
  if (order.kind === 'engage' && state.players) return !state.players.some(p => p.id === order.unitId && p.alive);
  if (order.kind === 'crew' && state.structures) { const st = state.structures.find(s => s.id === order.structureId); return !st || st.health <= 0 || !seatAt(st, order.seat); }
  if (order.kind === 'demolish' && state.structures) { const st = state.structures.find(s => s.id === order.structureId); return !st || st.health <= 0; }   // 69.5d: it fell
  if (order.kind === 'ride' && state.structures) { const st = state.structures.find(s => s.id === order.structureId); return !st || st.health <= 0 || (order.convoy?.leg === 'back' && st.atHome === true); }   // 63.1d step 4   // 69.4 step 5: a convoy's home leg is done once its truck stands home (core/convoys.ts `atHome`)
  return false;
}
